import moment from 'moment-mini'
import { combineLatest, Observable } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  tap
} from 'rxjs/operators'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { Member } from 'src/app/features/member/member'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { environment } from 'src/environments/environment'

import { Component, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngrx/store'

import { EmployeeBooking } from '../../employee-booking/employee-booking'
import { employeeBookingActions } from '../../employee-booking/employee-booking.actions'
import { employeeBookingSelectors } from '../../employee-booking/employee-booking.selectors'
import { employeeContractManagerLinkSelectors } from '../../employee-contract-manager-link/employee-contract-manager-link.selectors'
import {
  EmployeeContract,
  EmployeeContractCalculated
} from '../employee-contract'
import { employeeContractSelectors } from '../employee-contract.selectors'
import { EmployeeContractService } from '../employee-contract.service'

@UntilDestroy()
@Component({
  selector: 'employee-contract',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class EmployeeContractShowComponent implements OnInit {
  public contracts$: Observable<EmployeeContract[]>
  public member$: Observable<Member>
  public selectedContract$: Observable<EmployeeContract>
  public form: UntypedFormGroup
  public contractEmployeeBookings$: Observable<EmployeeBooking[]>
  public contractValues$: Observable<EmployeeContractCalculated>
  public employeeModuleConfig: any
  public amountOfEmployeeBookings: number
  public isLoading$: Observable<boolean>
  public selectedContract: EmployeeContract
  public contractSalary: number = 0
  public hidePeparationTimeColumn =
    environment.moduleConfigs.employeeModule
      .hideEmployeeContractsPreparationTimeColumn
  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<AppState>,
    public employeeContractService: EmployeeContractService
  ) {}

  ngOnInit() {
    this.employeeModuleConfig = environment.moduleConfigs.employeeModule

    this.isLoading$ = combineLatest([
      this.store.select(employeeBookingSelectors.selectIsLoading),
      this.store.select(employeeContractSelectors.selectIsLoading)
    ]).pipe(map(([a, b]) => a || b))

    this.form = this.fb.group({
      contractId: [null, []]
    })

    this.member$ = this.store.select(authSelectors.selectMember)

    this.contracts$ = combineLatest([
      this.store.select(employeeContractSelectors.selectAll),
      this.store.select(employeeContractManagerLinkSelectors.selectEntities),
      this.member$
    ]).pipe(
      map(
        ([employeeContracts, employeeContractManagerLinkEntities, member]) => {
          const availableContracts =
            this.employeeContractService.getAvailableContracts(
              employeeContracts,
              employeeContractManagerLinkEntities,
              member
            )

          if (
            availableContracts?.length == 1 &&
            this.form.controls.contractId.value != availableContracts[0].id
          ) {
            this.form.controls.contractId.setValue(availableContracts[0].id)
          }

          return availableContracts
        }
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    )

    const selectedContractId$ = this.form.controls.contractId.valueChanges.pipe(
      filter((contractId) => Boolean(contractId)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    )

    selectedContractId$.pipe(untilDestroyed(this)).subscribe((contractId) => {
      this.store.dispatch(
        employeeBookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              contractId
            },
            join: [{ field: 'product' }, { field: 'product.translations' }]
          })
        })
      )
    })

    this.selectedContract$ = selectedContractId$.pipe(
      switchMap((contractId) =>
        this.store.select(employeeContractSelectors.selectById, contractId)
      ),
      tap((selectedContract) => {
        this.selectedContract = selectedContract
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    )

    this.contractEmployeeBookings$ = this.selectedContract$.pipe(
      untilDestroyed(this),
      switchMap((contract) =>
        this.store
          .select(employeeBookingSelectors.selectByFilter, {
            field: 'contractId',
            value: contract.id
          })
          .pipe(
            map((employeeBookings) =>
              employeeBookings.filter((employeeBooking) => {
                const bookingStartDate = moment(employeeBooking.startDate)

                return (
                  bookingStartDate.isSameOrAfter(
                    moment(contract.startDate),
                    'day'
                  ) &&
                  (!contract.endDate ||
                    bookingStartDate.isSameOrBefore(
                      moment(contract.endDate),
                      'day'
                    ))
                )
              })
            )
          )
      )
    )

    this.contractValues$ = combineLatest([
      this.selectedContract$,
      this.contractEmployeeBookings$,
      this.store.select(productSelectors.selectEntities)
    ]).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
      untilDestroyed(this),
      map(([contract, contractEmployeeBookings, productEntities]) => {
        this.contractSalary = contract.salary
        this.amountOfEmployeeBookings = contractEmployeeBookings?.length
        return this.employeeContractService.calculateContractHours(
          contract,
          contractEmployeeBookings,
          productEntities
        )
      })
    )
  }
}
